import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import PartnerNavbar from './components/PartnerNavbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Preloader from './components/Preloader';
import HomePage from './pages/HomePage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import CalendarPage from './pages/CalendarPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/admin/master-admin/AdminPage';
import CreateEventPage from './pages/CreateEventPage';
import CreatePartnerPage from './pages/admin/master-admin/CreatePartnerPage';
import CreateBannerPage from './pages/CreateBannerPage';
import PartnerDetailPage from './pages/admin/master-admin/PartnerDetailPage';
import UserDetailPage from './pages/UserDetailPage';
import ContactDetailPage from './pages/ContactDetailPage';
import BannerDetailPage from './pages/BannerDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import FounderNotePage from './pages/FounderNotePage';
import PasswordResetPage from './pages/PasswordResetPage';
import PasswordResetConfirmPage from './pages/PasswordResetConfirmPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import NotFoundPage from './pages/NotFoundPage';
import PartnerDashboardPage from './pages/admin/partner-portal/PartnerDashboardPage';
import PartnerSubmitEventPage from './pages/admin/partner-portal/PartnerSubmitEventPage';
import PartnerProfilePage from './pages/admin/partner-portal/PartnerProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import PartnerPortalLandingPage from './pages/PartnerPortalLandingPage';
import PublicPartnerDetailPage from './pages/PublicPartnerDetailPage';
import PartnersPage from './pages/PartnersPage';

// ── Auth Guards ────────────────────────────────────────────────────
function RequireAuth({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    return children;
}

function RequireAdmin({ children }) {
    const { user, isAdmin, loading } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    if (!isAdmin) return <Navigate to="/" replace />;
    return children;
}

function RequirePartner({ children }) {
    const { user, isPartner, isAdmin, loading } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    if (!isPartner && !isAdmin) return <Navigate to="/" replace />;
    return children;
}
// ──────────────────────────────────────────────────────────────────

function AppContent() {
    const location = useLocation();
    const { user, isPartner, isAdmin } = useAuth();

    const noNavPages = ['/login', '/signup', '/change-password'];
    const hideAll = noNavPages.includes(location.pathname);

    const isPartnerRoute = location.pathname.startsWith('/partner');

    const showPartnerNav  = !hideAll && isPartner && !isAdmin;
    const showPublicNav   = !hideAll && !showPartnerNav && !isAdmin && !isPartnerRoute;
    const showAdminFooter = isPartnerRoute || isAdmin;

    return (
        <>
            <Preloader />
            <ScrollToTop />
            {showPartnerNav && <PartnerNavbar />}
            {showPublicNav && <Navbar />}
            <Routes>
                {/* ── Public ── */}
                <Route path="/" element={<HomePage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/events/:id" element={<EventDetailPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/founder-note" element={<FounderNotePage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/reset-password" element={<PasswordResetPage />} />
                <Route path="/reset-password/:token" element={<PasswordResetConfirmPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />

                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/partners/:id" element={<PublicPartnerDetailPage />} />

                {/* ── Partner Portal CTA (public landing) ── */}
                <Route path="/partner-portal" element={<PartnerPortalLandingPage />} />

                {/* ── Requires login ── */}
                <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
                <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />

                {/* ── Admin only ── */}
                <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />
                <Route path="/admin/events/create" element={<RequireAdmin><CreateEventPage /></RequireAdmin>} />
                <Route path="/admin/events/edit/:id" element={<RequireAdmin><CreateEventPage /></RequireAdmin>} />
                <Route path="/admin/partners/create" element={<RequireAdmin><CreatePartnerPage /></RequireAdmin>} />
                <Route path="/admin/partners/edit/:id" element={<RequireAdmin><CreatePartnerPage /></RequireAdmin>} />
                <Route path="/admin/partners/:id" element={<RequireAdmin><PartnerDetailPage /></RequireAdmin>} />
                <Route path="/admin/banners/create" element={<RequireAdmin><CreateBannerPage /></RequireAdmin>} />
                <Route path="/admin/banners/edit/:id" element={<RequireAdmin><CreateBannerPage /></RequireAdmin>} />
                <Route path="/admin/banners/:id" element={<RequireAdmin><BannerDetailPage /></RequireAdmin>} />
                <Route path="/admin/users/:id" element={<RequireAdmin><UserDetailPage /></RequireAdmin>} />
                <Route path="/admin/contacts/:id" element={<RequireAdmin><ContactDetailPage /></RequireAdmin>} />

                {/* ── Partner (or admin) ── */}
                <Route path="/partner" element={<RequirePartner><PartnerDashboardPage /></RequirePartner>} />
                <Route path="/partner/dashboard" element={<RequirePartner><PartnerDashboardPage /></RequirePartner>} />
                <Route path="/partner/submit" element={<RequirePartner><PartnerSubmitEventPage /></RequirePartner>} />
                <Route path="/partner/events/edit/:id" element={<RequirePartner><PartnerSubmitEventPage /></RequirePartner>} />
                <Route path="/partner/profile" element={<RequirePartner><PartnerProfilePage /></RequirePartner>} />

                {/* ── 404 ── */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
            {!hideAll && !showAdminFooter && <Footer />}
        </>
    );
}

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppContent />
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
